/**
 * TemplateStepAiPanel — "Help me write" for ONE template step (plan §3.4, owner
 * answer 11: AI fills a single step, never the whole template). The owner of
 * the step supplies `onFill`, which asks the fill-step route and applies the
 * answer; it rejects when the AI fails. A failure is shown here as an error
 * with Retry (when a retry can help) and nothing is written into the step —
 * template text is never passed off as the AI's.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { Atom } from '../../../design-system/icons';
import { describeFillError, type FillErrorView } from './templateFillError';

/** The fill-step route's instruction cap (runes). */
const MAX_INSTRUCTION_LENGTH = 5000;

export interface TemplateStepAiPanelProps {
  stepNo: number;
  onFill: (instruction: string) => Promise<void>;
  onClose: () => void;
}

export function TemplateStepAiPanel({ stepNo, onFill, onClose }: TemplateStepAiPanelProps) {
  const uid = useId();
  const [instruction, setInstruction] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<FillErrorView | null>(null);
  // Closing or finishing while a request is out must not set state on a gone panel.
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const lastInstruction = useRef('');

  const run = async (text: string) => {
    lastInstruction.current = text;
    setBusy(true);
    setError(null);
    try {
      await onFill(text);
      if (alive.current) onClose();
    } catch (err) {
      if (alive.current) setError(describeFillError(err));
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  return (
    <section
      aria-label={`Help me write step ${stepNo}`}
      className="flex flex-col gap-3 rounded-xl border border-(--border-brand-default) bg-linear-to-b from-[#fdfcff] to-[#eeebfb] p-3"
    >
      <div className="flex items-center justify-between">
        <span className="text-sm/5 text-[#5a07ce]">Help me write</span>
        <button type="button" disabled={busy} className="text-sm/5 text-[#5a07ce] hover:underline disabled:opacity-50" onClick={onClose}>
          Close
        </button>
      </div>
      <div className="flex items-end gap-2">
        <textarea
          id={`${uid}-instruction`}
          aria-label="What should this step say?"
          rows={2}
          maxLength={MAX_INSTRUCTION_LENGTH}
          value={instruction}
          disabled={busy}
          placeholder="Optional: what should this step say?"
          onChange={(e) => setInstruction(e.target.value)}
          className="min-h-9 flex-1 resize-none rounded-md border border-border bg-card px-3 py-2 text-sm/5"
        />
        <button
          type="button"
          disabled={busy}
          onClick={() => void run(instruction.trim())}
          className="inline-flex h-11 shrink-0 items-center gap-2 rounded-full bg-foreground px-4 text-sm text-background disabled:opacity-50"
        >
          <Atom size={20} aria-hidden />
          {busy ? 'Writing…' : 'Write it'}
        </button>
      </div>
      {error ? (
        <p role="alert" className="flex flex-wrap items-center gap-2 text-xs text-destructive">
          <span>{error.message}</span>
          {error.retryable ? (
            <button type="button" disabled={busy} onClick={() => void run(lastInstruction.current)} className="underline underline-offset-2">
              Retry
            </button>
          ) : null}
        </p>
      ) : null}
      <p className="text-xs/4 text-muted-foreground">
        Writes this step from your text and the earlier steps. Nothing is saved until you save the template.
      </p>
    </section>
  );
}
